import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, ArrowRight, X } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetClose,
} from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { useDemoModal } from "./DemoModal";

const NAV = [
    {
        label: "Product",
        testid: "nav-link-product",
        items: [
            ["CRM", "#features"],
            ["Lead Management", "#lead-management"],
            ["Sales Pipeline", "#pipeline"],
            ["Automation", "#automation"],
            ["Field Sales", "#field-sales"],
            ["Engage", "#engage"],
            ["Email Marketing", "#engage"],
            ["WhatsApp Marketing", "#engage"],
            ["Reports & Analytics", "#ecosystem"],
        ],
    },
    {
        label: "Solutions",
        testid: "nav-link-solutions",
        items: [
            ["Real Estate", "#industries"],
            ["Staffing & Recruitment", "#industries"],
            ["B2B Sales", "#industries"],
            ["Agencies", "#industries"],
            ["Growing Businesses", "#industries"],
        ],
    },
    {
        label: "Resources",
        testid: "nav-link-resources",
        items: [
            ["Features", "#features"],
            ["Guides", "#manifesto"],
            ["Blog", "/blog"],
            ["FAQs", "#faq"],
        ],
    },
    {
        label: "Company",
        testid: "nav-link-company",
        items: [
            ["About Kryvo", "#ecosystem"],
            ["Contact", "#footer"],
            ["Careers", "#footer"],
        ],
    },
];

const Dropdown = ({ group }) => {
    const [open, setOpen] = useState(false);
    return (
        <div
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                data-testid={group.testid}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                    open ? "bg-[#F0FDF4] text-[#15803D]" : "text-[#334155] hover:text-[#15803D]"
                }`}
            >
                {group.label}
                <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        className="absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-2"
                    >
                        <div className="grid grid-cols-1 gap-0.5 rounded-2xl border border-[#E5E7EB] bg-white p-2 shadow-xl shadow-slate-900/5">
                            {group.items.map(([label, href]) => (
                                <a
                                    key={label}
                                    href={href}
                                    onClick={() => setOpen(false)}
                                    className="rounded-lg px-3.5 py-2.5 text-sm font-medium text-[#334155] transition-colors hover:bg-[#F0FDF4] hover:text-[#15803D]"
                                >
                                    {label}
                                </a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const Navbar = () => {
    const { openDemo } = useDemoModal();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="header-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "border-b border-[#E5E7EB]/80 bg-white/85 shadow-sm backdrop-blur-xl"
                    : "bg-white/60 backdrop-blur-md"
            }`}
        >
            <nav className="mx-auto flex h-[68px] w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" data-testid="nav-logo-kryvo" aria-label="Kryvo CRM home">
                    <Logo />
                </Link>

                <div className="hidden items-center gap-0.5 lg:flex">
                    {NAV.map((g) => (
                        <Dropdown key={g.label} group={g} />
                    ))}
                    <a
                        href="#pricing"
                        data-testid="nav-link-pricing"
                        className="rounded-full px-3.5 py-2 text-sm font-medium text-[#334155] transition-colors hover:text-[#15803D]"
                    >
                        Pricing
                    </a>
                </div>

                <div className="hidden items-center gap-2.5 lg:flex">
                    <Link
                        to="/signup"
                        data-testid="nav-btn-start-free"
                        className="group flex items-center gap-1.5 rounded-full bg-[#16A34A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-[#16A34A]/25 transition-all hover:bg-[#15803D] hover:shadow-md hover:shadow-[#16A34A]/30"
                    >
                        Start Free
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                </div>

                <Sheet>
                    <SheetTrigger asChild>
                        <button
                            data-testid="nav-mobile-menu-button"
                            aria-label="Open menu"
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E5E7EB] text-[#111827] lg:hidden"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[86vw] max-w-sm overflow-y-auto bg-white p-0">
                        <div className="flex items-center justify-between border-b border-[#E5E7EB] px-5 py-4">
                            <Logo />
                            <SheetClose asChild>
                                <button aria-label="Close menu" data-testid="nav-mobile-close-button" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E7EB]">
                                    <X className="h-4 w-4" />
                                </button>
                            </SheetClose>
                        </div>
                        <div className="px-5 py-4">
                            {NAV.map((g) => (
                                <div key={g.label} className="py-2">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#94A3B8]">
                                        {g.label}
                                    </p>
                                    <div className="mt-2 grid gap-1">
                                        {g.items.map(([label, href]) => (
                                            <SheetClose asChild key={label}>
                                                <a
                                                    href={href}
                                                    className="rounded-lg px-2 py-2 text-[15px] font-medium text-[#334155] hover:bg-[#F0FDF4] hover:text-[#15803D]"
                                                >
                                                    {label}
                                                </a>
                                            </SheetClose>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <SheetClose asChild>
                                <a href="#pricing" className="mt-2 block rounded-lg px-2 py-2 text-[15px] font-semibold text-[#111827]">
                                    Pricing
                                </a>
                            </SheetClose>
                            <div className="mt-6 grid gap-3 border-t border-[#E5E7EB] pt-6">
                                <SheetClose asChild>
                                    <Link to="/signup" data-testid="nav-mobile-btn-start-free" className="rounded-full bg-[#16A34A] px-5 py-3 text-center text-sm font-semibold text-white">
                                        Start Free →
                                    </Link>
                                </SheetClose>
                                <SheetClose asChild>
                                    <button onClick={openDemo} data-testid="nav-mobile-btn-book-demo" className="rounded-full bg-[#111827] px-5 py-3 text-sm font-semibold text-white">
                                        Book a Demo
                                    </button>
                                </SheetClose>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </nav>
        </header>
    );
};

export default Navbar;
